import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { collectionRequestAPI } from '../services/api';
import { LoadingSpinner } from '../components/LoadingSpinner';
import './AnalysisPage.css';

interface ChartDataPoint {
  period: string;
  collections: number;
  volume: number;
  cost: number;
}

interface PeriodStats {
  average_per_period: number;
  best_period: { period: string; value: number };
  worst_period: { period: string; value: number };
  growth_rate: number;
  period_label: string;
}

interface AnalysisData {
  totalCollections: number;
  totalVolume: number;
  totalCost: number;
  averageVolume: number;
  chartData: ChartDataPoint[];
  chartType: string;
  period: string;
  periodStats: PeriodStats;
  costBreakdown: {
    baseRate: number;
    additionalFees: number;
    discounts: number;
  };
  environmentalImpact: {
    co2Saved: number;
    waterSaved: number;
    energySaved: number;
  };
}

export function AnalysisPage() {
  const [data, setData] = useState<AnalysisData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<string>('6months');
  const [chartType, setChartType] = useState<string>('monthly');
  const [chartMetric, setChartMetric] = useState<string>('volume');

  // 분석 기간에 따른 사용 가능한 차트 타입 계산
  const getAvailableChartTypes = (period: string) => {
    switch (period) {
      case '1week':
        return ['weekly'];
      case '1month':
        return ['weekly', 'monthly'];
      case '3months':
        return ['weekly', 'monthly', 'quarterly'];
      case '6months':
      case '1year':
        return ['weekly', 'monthly', 'quarterly', 'yearly'];
      default:
        return ['monthly'];
    }
  };

  // 기간 변경 시 차트 타입 자동 조정
  const handlePeriodChange = (newPeriod: string) => {
    const availableTypes = getAvailableChartTypes(newPeriod);
    setSelectedPeriod(newPeriod);
    
    // 현재 차트 타입이 새 기간에서 사용 불가능하면 첫 번째 옵션으로 변경
    if (!availableTypes.includes(chartType)) {
      setChartType(availableTypes[0]);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        console.log('Loading analysis data for period:', selectedPeriod, 'chartType:', chartType);
        const analysisData = await collectionRequestAPI.getAnalysis(selectedPeriod, chartType);
        console.log('Received analysis data:', analysisData);
        
        // 데이터 검증
        if (analysisData && typeof analysisData === 'object') {
          // 필수 필드들이 존재하는지 확인
          const requiredFields = ['totalCollections', 'totalVolume', 'totalCost', 'averageVolume', 'chartData', 'periodStats'];
          const missingFields = requiredFields.filter(field => !(field in analysisData));
          
          if (missingFields.length > 0) {
            throw new Error(`응답 데이터에 필수 필드가 없습니다: ${missingFields.join(', ')}`);
          }
          
          setData(analysisData);
        } else {
          throw new Error('올바르지 않은 응답 형식입니다.');
        }
      } catch (err: any) {
        console.error('Failed to load analysis data:', err);
        console.error('Error response data:', err.response?.data);
        console.error('Error detail array:', err.response?.data?.detail);
        
        // FastAPI validation 에러 처리
        if (err.response?.data?.detail && Array.isArray(err.response.data.detail)) {
          console.error('Validation errors:', err.response.data.detail);
          const validationErrors = err.response.data.detail.map((error: any) => {
            console.error('Individual error:', error);
            return `${error.loc?.join?.('.') || 'unknown'}: ${error.msg || 'unknown error'}`;
          }).join(', ');
          setError(`입력 오류: ${validationErrors}`);
        } else {
          setError(err.response?.data?.detail || '분석 데이터를 불러오는 중 오류가 발생했습니다.');
        }
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [selectedPeriod, chartType]);

  const formatPeriodLabel = (period: string) => {
    // 기간별 라벨 포맷팅
    if (period.includes('Q')) {
      return period; // 2025Q1 형식 그대로
    } else if (period.includes('-')) {
      const [year, month] = period.split('-');
      return `${month}월`; // 01월 형식
    } else if (period.includes('/')) {
      return period; // 01/15 형식 그대로
    } else {
      return period; // 2025 형식 그대로
    }
  };

  const getChartColor = () => {
    switch (chartMetric) {
      case 'volume': return '#3b82f6';
      case 'collections': return '#10b981';
      case 'cost': return '#f59e0b';
      default: return '#3b82f6';
    }
  };

  const getMetricValue = (item: ChartDataPoint) => {
    switch (chartMetric) {
      case 'volume': return item.volume;
      case 'collections': return item.collections;
      case 'cost': return item.cost / 1000; // 천원 단위
      default: return item.volume;
    }
  };

  const getMetricLabel = () => {
    switch (chartMetric) {
      case 'volume': return '수거량 (L)';
      case 'collections': return '수거 횟수 (회)';
      case 'cost': return '비용 (천원)';
      default: return '수거량 (L)';
    }
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="custom-tooltip">
          <p className="tooltip-label">{`기간: ${formatPeriodLabel(label)}`}</p>
          <p className="tooltip-volume">{`수거량: ${data.volume}L`}</p>
          <p className="tooltip-collections">{`수거 횟수: ${data.collections}회`}</p>
          <p className="tooltip-cost">{`비용: ₩${data.cost.toLocaleString()}`}</p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="analysis-page">
        <div className="loading-container">
          <LoadingSpinner size="large" />
          <p>분석 데이터를 계산하는 중...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="analysis-page">
        <div className="error-container">
          <div className="error-icon">📊</div>
          <h3>데이터를 불러올 수 없습니다</h3>
          <p>{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="retry-btn"
            style={{ 
              marginTop: '1rem', 
              padding: '0.5rem 1rem', 
              backgroundColor: '#3b82f6', 
              color: 'white', 
              border: 'none', 
              borderRadius: '0.375rem', 
              cursor: 'pointer' 
            }}
          >
            다시 시도
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="analysis-page">
        <div className="error-container">
          <div className="error-icon">📊</div>
          <h3>데이터를 불러올 수 없습니다</h3>
          <p>분석할 데이터가 충분하지 않습니다.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="analysis-page">
      <div className="analysis-container">
        <div className="analysis-header">
          <h1>📊 수거 분석</h1>
          <p>음식물 폐기물 수거 현황과 환경적 효과를 분석합니다.</p>
        </div>

        <div className="analysis-controls">
          <div className="control-group">
            <label htmlFor="periodSelect">분석 기간</label>
            <select
              id="periodSelect"
              value={selectedPeriod}
              onChange={(e) => handlePeriodChange(e.target.value)}
              className="period-select"
            >
              <option value="1week">최근 1주</option>
              <option value="1month">최근 1개월</option>
              <option value="3months">최근 3개월</option>
              <option value="6months">최근 6개월</option>
              <option value="1year">최근 1년</option>
            </select>
          </div>

          <div className="control-group">
            <label htmlFor="chartTypeSelect">기간 단위</label>
            <select
              id="chartTypeSelect"
              value={chartType}
              onChange={(e) => setChartType(e.target.value)}
              className="chart-select"
            >
              {getAvailableChartTypes(selectedPeriod).map(type => (
                <option key={type} value={type}>
                  {type === 'weekly' ? '주별' : 
                   type === 'monthly' ? '월별' : 
                   type === 'quarterly' ? '분기별' : '연도별'}
                </option>
              ))}
            </select>
          </div>

          <div className="control-group">
            <label htmlFor="metricSelect">표시 지표</label>
            <select
              id="metricSelect"
              value={chartMetric}
              onChange={(e) => setChartMetric(e.target.value)}
              className="metric-select"
            >
              <option value="volume">수거량 (L)</option>
              <option value="collections">수거 횟수</option>
              <option value="cost">비용</option>
            </select>
          </div>
        </div>

        {/* 요약 통계 */}
        <div className="analysis-summary">
          <div className="summary-cards">
            <div className="summary-card">
              <div className="card-icon">📦</div>
              <div className="card-content">
                <h3>총 수거 횟수</h3>
                <div className="card-value">{data.totalCollections}회</div>
                <div className="card-trend">
                  {(() => {
                    const activeMonths = data.chartData.filter(item => item.collections > 0).length;
                    const totalMonths = data.chartData.length;
                    return activeMonths > 0 ? `${activeMonths}/${totalMonths} 기간 활동` : '활동 없음';
                  })()}
                </div>
              </div>
            </div>

            <div className="summary-card">
              <div className="card-icon">📊</div>
              <div className="card-content">
                <h3>총 수거량</h3>
                <div className="card-value">{data.totalVolume}L</div>
                <div className="card-trend">
                  평균 {data.averageVolume.toFixed(1)}L/회
                </div>
              </div>
            </div>

            <div className="summary-card">
              <div className="card-icon">💰</div>
              <div className="card-content">
                <h3>총 비용</h3>
                <div className="card-value">₩{data.totalCost.toLocaleString()}</div>
                <div className="card-trend">
                  평균 ₩{(data.totalCost / data.totalCollections).toFixed(0)}/회
                </div>
              </div>
            </div>

            <div className="summary-card environmental">
              <div className="card-icon">🌱</div>
              <div className="card-content">
                <h3>환경 효과</h3>
                <div className="card-value">{data.environmentalImpact.co2Saved}kg</div>
                <div className="card-trend">
                  CO₂ 절약량
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 차트 섹션 */}
        <div className="chart-section">
          <div className="chart-header">
            <h2>{getMetricLabel()} 추이</h2>
            <div className="chart-legend">
              <div className="legend-item">
                <div className="legend-color primary"></div>
                <span>{getMetricLabel()}</span>
              </div>
            </div>
          </div>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.2)" />
                <XAxis 
                  dataKey="period" 
                  tick={{ fill: 'white' }}
                  tickFormatter={formatPeriodLabel}
                />
                <YAxis tick={{ fill: 'white' }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar 
                  dataKey={chartMetric} 
                  fill={getChartColor()}
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* 비용 분석 */}
        <div className="cost-analysis">
          <h2>비용 분석</h2>
          <div className="cost-breakdown">
            <div className="cost-item">
              <div className="cost-label">기본 요금</div>
              <div className="cost-bar-container">
                <div 
                  className="cost-bar base" 
                  style={{ width: `${(data.costBreakdown.baseRate / data.totalCost) * 100}%` }}
                ></div>
                <div className="cost-value">₩{data.costBreakdown.baseRate.toLocaleString()}</div>
              </div>
            </div>
            <div className="cost-item">
              <div className="cost-label">추가 요금</div>
              <div className="cost-bar-container">
                <div 
                  className="cost-bar additional" 
                  style={{ width: `${(data.costBreakdown.additionalFees / data.totalCost) * 100}%` }}
                ></div>
                <div className="cost-value">₩{data.costBreakdown.additionalFees.toLocaleString()}</div>
              </div>
            </div>
            <div className="cost-item">
              <div className="cost-label">할인</div>
              <div className="cost-bar-container">
                <div 
                  className="cost-bar discount" 
                  style={{ width: `${(data.costBreakdown.discounts / data.totalCost) * 100}%` }}
                ></div>
                <div className="cost-value">-₩{data.costBreakdown.discounts.toLocaleString()}</div>
              </div>
            </div>
          </div>
        </div>

        {/* 주간 트렌드 */}
        <div className="trends-section">
          <h2>주간 트렌드</h2>
          <div className="weekly-trends">
            {data.chartData.slice(-4).map((item, index) => (
              <div key={index} className="weekly-item">
                <div className="week-label">{formatPeriodLabel(item.period)}</div>
                <div className="week-bar-container">
                  <div 
                    className="week-bar" 
                    style={{ width: `${(item.volume / Math.max(...data.chartData.map(d => d.volume))) * 100}%` }}
                  ></div>
                  <div className="week-value">{item.volume}L</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 기간별 상세 통계 */}
        <div className="period-stats-section">
          <div className="stats-header">
            <h2>{data.periodStats.period_label}별 통계</h2>
            <p>선택한 기간의 상세한 분석 결과입니다.</p>
          </div>
          
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon">📊</div>
              <div className="stat-content">
                <h3>평균 {data.periodStats.period_label}</h3>
                <div className="stat-value">{data.periodStats.average_per_period.toFixed(1)}L</div>
                <div className="stat-description">
                  {(() => {
                    const activePeriods = data.chartData.filter(item => item.volume > 0).length;
                    return activePeriods > 0 ? `${activePeriods}개 ${data.periodStats.period_label} 평균` : '데이터 없음';
                  })()}
                </div>
              </div>
            </div>

            <div className="stat-card best">
              <div className="stat-icon">🏆</div>
              <div className="stat-content">
                <h3>최고 실적 {data.periodStats.period_label}</h3>
                <div className="stat-value">{data.periodStats.best_period.value}L</div>
                <div className="stat-description">{formatPeriodLabel(data.periodStats.best_period.period)}</div>
              </div>
            </div>

            <div className="stat-card worst">
              <div className="stat-icon">📉</div>
              <div className="stat-content">
                <h3>최저 실적 {data.periodStats.period_label}</h3>
                <div className="stat-value">{data.periodStats.worst_period.value}L</div>
                <div className="stat-description">{formatPeriodLabel(data.periodStats.worst_period.period)}</div>
              </div>
            </div>

            <div className={`stat-card ${data.periodStats.growth_rate >= 0 ? 'growth-positive' : 'growth-negative'}`}>
              <div className="stat-icon">{data.periodStats.growth_rate >= 0 ? '📈' : '📉'}</div>
              <div className="stat-content">
                <h3>성장률</h3>
                <div className="stat-value">
                  {data.periodStats.growth_rate >= 0 ? '+' : ''}{data.periodStats.growth_rate}%
                </div>
                <div className="stat-description">
                  {data.periodStats.growth_rate >= 0 ? '증가' : '감소'} 추세
                </div>
              </div>
            </div>
          </div>

          {/* 기간별 상세 테이블 */}
          <div className="period-table">
            <h3>{data.periodStats.period_label}별 상세 내역</h3>
            <div className="table-container">
              <table className="stats-table">
                <thead>
                  <tr>
                    <th>기간</th>
                    <th>수거량 (L)</th>
                    <th>수거 횟수</th>
                    <th>비용 (원)</th>
                    <th>단위당 평균</th>
                  </tr>
                </thead>
                <tbody>
                  {data.chartData.map((item, index) => (
                    <tr key={index} className={item.volume === data.periodStats.best_period.value ? 'highlight-best' : 
                                              item.volume === data.periodStats.worst_period.value ? 'highlight-worst' : ''}>
                      <td>{formatPeriodLabel(item.period)}</td>
                      <td className="volume-cell">{item.volume}L</td>
                      <td className="collections-cell">{item.collections}회</td>
                      <td className="cost-cell">₩{item.cost.toLocaleString()}</td>
                      <td className="average-cell">
                        {item.collections > 0 ? `${(item.volume / item.collections).toFixed(1)}L/회` : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* 환경 효과 */}
        <div className="environmental-impact">
          <h2>환경적 효과</h2>
          <div className="impact-grid">
            <div className="impact-card">
              <div className="impact-icon">🌍</div>
              <h3>CO₂ 절약</h3>
              <div className="impact-value">{data.environmentalImpact.co2Saved}kg</div>
              <p>음식물 폐기물을 매립하지 않고 재활용함으로써 온실가스 배출을 줄였습니다.</p>
            </div>

            <div className="impact-card">
              <div className="impact-icon">💧</div>
              <h3>물 절약</h3>
              <div className="impact-value">{data.environmentalImpact.waterSaved}L</div>
              <p>음식물 폐기물 처리 과정에서 절약된 물의 양입니다.</p>
            </div>

            <div className="impact-card">
              <div className="impact-icon">⚡</div>
              <h3>에너지 절약</h3>
              <div className="impact-value">{data.environmentalImpact.energySaved}kWh</div>
              <p>재활용을 통해 절약된 에너지입니다.</p>
            </div>
          </div>
        </div>

        {/* 추천사항 */}
        <div className="recommendations">
          <h2>개선 추천사항</h2>
          <div className="recommendation-list">
            <div className="recommendation-item">
              <div className="rec-icon">📅</div>
              <div className="rec-content">
                <h4>정기적인 수거 일정</h4>
                <p>매주 같은 요일에 수거를 진행하여 일관된 패턴을 유지하세요. 이는 효율적인 폐기물 관리를 도와줍니다.</p>
              </div>
            </div>
            <div className="recommendation-item">
              <div className="rec-icon">♻️</div>
              <div className="rec-content">
                <h4>분리 배출 최적화</h4>
                <p>음식물과 일반 쓰레기를 철저히 분리하여 재활용률을 높이고 환경에 기여하세요.</p>
              </div>
            </div>
            <div className="recommendation-item">
              <div className="rec-icon">💰</div>
              <div className="rec-content">
                <h4>비용 효율성 개선</h4>
                <p>수거량을 적절히 조절하여 비용을 최적화하고, 할인 혜택을 활용하세요.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}